
/**
 *      Data-driven UI logic that processes Search API response
 *
 *      @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *      Created on 06/02/2017
 *      Modified on 04/09/2020
 */


/**
 * Make an AJAX call
 * @param url {string} - request url
 * @param successCallback {function} - success callback function
 * @param failureCallback {function} - failure callback function
 */
function makeAjaxRequest( url, successCallback, failureCallback ) {
    var data = {};

    // Check if URL is of type array
    if ( Array.isArray( url ) ) {
        // Extract url from the array
        if ( url && url.length === 1 ) {
            url = url[ 0 ];
        }
    }
    if ( isValidAjaxUrl( url ) ) {
        $.ajax({
            url: url,
            data: data,
            dataType: 'json'
        })
        .done( successCallback )
        .fail( failureCallback );
    } else {
        failureCallback();
    }
}

/**
 * Make an AJAX call multiple times.
 * @param url {string} Request url.
 * @param successCallback {function} Success callback function.
 * @param failureCallback {function} Failure callback function.
 * @param tryNumber {number} Number of times the function has been called.
 * @param maxTries {number} Maximum number of times the function will be called.
 */
function makeAjaxRequestWithRetry( url, successCallback, failureCallback, tryNumber, maxTries ) {

    var data = {};

    // Check if URL is of type array
    if ( Array.isArray( url ) ) {
        // Extract url from the array
        if ( url && url.length === 1 ) {
            url = url[ 0 ];
        }
    }
    if ( isValidAjaxUrl( url ) ) {
        $.ajax({
            url: url,
            data: data,
            dataType: 'json'
        })
        .done( successCallback )
        .fail( function() {
            // Try again if number of times the function has been called is less than maximum number of tries
            if ( tryNumber <= maxTries ) {
                makeAjaxRequestWithRetry( url, successCallback, failureCallback, tryNumber + 1, maxTries );
            } else {
                (failureCallback)();
            }
        });
    } else {
        failureCallback();
    }
}

/**
 * Check if Ajax URL has a valid format
 * @param url {string} - Ajax call URL
 * @return {boolean}
 */
function isValidAjaxUrl( url ) {
    var result = false;

    // URL must start with a forward slash
    if ( url && typeof url === "string" && url.startsWith('/') ) {
        result = true;
    }
    return result;
}

/**
 * Store Search API response for a section. Once all data has been fetched, display Search API response.
 * @param name {string} - section name to store
 * @param names {[string]} - array of data names from API model
 * @param urls {[string]} - array of data URLs
 * @param response {Object} - ES response object
 * @param pageName {string} - name of the page
 * @param viewModel {Backbone.Model} - region view model
 * @param page {number} - page number
 * @returns {Function} - callback function with response
 */
function storeResponse( name, names, urls, response, pageName, viewModel, page ) {
    return function( data, textStatus, jqXHR ) {
        var pageModel,
            dependentUrls,
            dependentName,
            i,
            j;

        if ( name && GlobalPageState && !( response.hasOwnProperty( name ) ) ) {
            // Store data into a variable
            response[name] = {};
            response[name] = data;

            if ( names.length > urls.length ) {
                pageModel = new BackboneModel();
                pageModel.set("page", page);
                dependentUrls = constructDependentUrls( name, data, names, pageName, pageModel );
                for ( i = 0; i < dependentUrls.length; i++ ) {
                    urls.push(dependentUrls[i]);
                    dependentName = names[urls.length + i - 1];
                    makeAjaxRequest( dependentUrls[i], storeResponse( dependentName, names, urls, response, pageName, viewModel, page), dataNotFound( dependentUrls[ i ]) );
                }
            }

            if ( GlobalPageState ) {
                if ( GlobalPageState.data && ( ! GlobalPageState.data[ name ] ) ) {
                    GlobalPageState.data[ name ] = response[ name ];
                }

                if ( hasKeys( response ) && Object.keys( response ).length === urls.length && names.length === urls.length ) {

                    // store response into the global page state
                    for ( j = 0; j < names.length; j++ ) {
                        if ( names[j] === name ) {
                            GlobalPageState.data[ name ] = response[ name ];
                        }
                    }
                    // display single record page
                    displayDetailsPage( response, viewModel, names, urls );
                }
            }
        }
    };
}

/**
 * Display data inside the region inside a layout
 * @param data {object} - data object that contains Search API data, i.e. { "metadata": { ... }, "siblings": { ... } ... }
 * @param viewModel {Backbone.Model} - page view model
 * @param dataNames {[string]} - data section names from API model
 * @param jsonModel {object} - data JSON model
 * @param urls {[string]} - array of data URLs
 */
function displayResponse( data, viewModel, dataNames, jsonModel, urls ) {

    var viewRegions,
        regionLayout,
        regionName,
        fieldsMatch,
        viewRegion,
        layoutView,
        dataNamesMatch,
        i;

    if (viewModel) {

        viewRegions = viewModel.get("regions");

        if (viewRegions) {
            // Check that the data object keys match the dataNames array
            dataNamesMatch = doAPINamesMatch(data, dataNames);
            if (dataNamesMatch) {
                for (i = 0; i < viewRegions.length; i++) {

                    viewRegion = viewRegions[i];

                    // Check that the region fields match the data object
                    fieldsMatch = doLayoutFieldsMatch(data, viewRegion);
                    if (fieldsMatch) {


                        // Render data into the layout region
                        regionName = viewRegion.name;
                        // regionLayout = getRegionLayout(viewRegion, data, layoutView, jsonModel);

                        if (viewRegion && viewRegion.graph && viewRegion.graph.toString() === "true") {
                            displayTreeRegion(viewRegion, data, urls);
                        }

                        layoutView = GlobalPageState.layout;
                        regionLayout = getRegionLayout(viewRegions[i], data, layoutView, jsonModel);

                        if (regionLayout && regionName && layoutView) {
                            layoutView.getRegion(regionName).show(regionLayout);
                        }
                    }
                }
            }
        }
    }

        // hide loading icons
        hideSearchLoadingIcon('search-loading-icon');
        hideSearchLoadingIcon('facets-loading-icon');
}


/**
 * Display search results
 * @param jsonModel {object} - search UI model
 * @param recordCollection {BackboneCollection} - search result as a Backbone.Collection object
 * @param nRecords {number} - total number of search results
 */
function displaySearchResponse( jsonModel, recordCollection, nRecords ){

    var snippet,
        items,
        snippetCollection,
        recordsView,
        nPixels = 45;  /* Maximum height of each line */

    snippet = new BackboneModel();

    // Create a Backbone model of the search snippet
    if ( jsonModel && App && App.globalState && App.globalState.snippets ) {

        // Get items to include in the search snippet
        items = getSnippetItems( jsonModel, recordCollection );
        snippet.set( "items", items );

        // Set item score attribute
        var recordModel = lookupFieldModel( recordCollection, "score" );
        if ( recordModel ) {
            snippet.set("score", recordModel.get("value"));
        }


        // Add snippets to a global state
        App.globalState.snippets.push( snippet );

        // Display the layout with the full collection
        if ( nRecords === App.globalState.snippets.length ) {

            // To ensure the same order of results, first order results by their titles, then order results by their scores.
            // Results with the same score will be sorted in the same way, by their title

            // Order search snippets by their titles
           // App.globalState.snippets.sort( compareByTitle );

            // Order search snippets by their score attribute
            App.globalState.snippets.sort( compareByScore );

            snippetCollection = new BackboneCollection();
            snippetCollection.reset( App.globalState.snippets );

            recordsView = new RecordsListView( { collection: snippetCollection } );
            if (  App.globalState.layout && App.globalState.layout.getRegion( 'resultsRegion' ) && $ ( App.globalState.layout.getRegion( 'resultsRegion' ).el ).length > 0 ) {
                App.globalState.layout.getRegion( 'resultsRegion' ).show( recordsView );

                /* Clamp text longer than 45px (or 2 lines) */
                // $( '.item' ).dotdotdot({
                //     height: nPixels
                // });

                // Hide loading icons
                hideLoadingIcon('loading-icon');
                hideSearchLoadingIcon( 'facets-loading-icon' );

                // Hide any errors if they were shown before
                $('#error-region').hide();

                // Display left border
                // $("#search-results-region").css('border-left', 'solid 1px lightgray');

            }
        }
    }
}

/**
 * Parse ES data object and get response object array
 * @param data {object} - search result data object
 * @returns {*} - ES response object array
 */
function getResponseObject( data ) {
    var response;
    if ( data && data.hits && data.hits.hits ) {
        response = data.hits.hits;
    }
    return response;
}

/**
 * Parse ES data object and get response object
 * @param data {Object} - search result data object
 * @returns {*} - ES response object
 */
function getResponseHits( data ) {
    var response;
    if ( data && data.hits ) {
        response = data.hits;
    }
    return response;
}

/**
 * Get the total number of Search API results
 * @param data {object} - search result data object
 * @returns {*}
 */
function getNumberOfHits( data ) {
    var result = 0,
        hits;

    if ( data ) {
        hits = getResponseHits( data );
        if ( hits ) {
            result = hits.total;
        }
    }
    return result;
}

/**
 * Extract a field value from ElasticSearch response
 * @param field {string} - name of the field to look for
 * @param response {object} - ElasticSearch response JavaScript object
 * @return {string|null} - returns the field value if it is found
 */
function getElasticSearchField( field, response ) {

    var result = null,
        source,
        results,
        i;

    if ( response && response.hits ) {
        if ( field === "total" ) {
            result = response.hits.total;
        } else if ( response.hits.hits ) {
            results = response.hits.hits;
            for ( i = 0; i < results.length; i++ ) {
                if ( field === "_type" || field === "_id" ) {
                    source = results[ i ];
                } else {
                    source = results[ i ]._source;
                }
                result = source[ field ];
            }
        }
    }

    return result;
}

